import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Search, 
  Briefcase, 
  TrendingUp, 
  Users, 
  Target, 
  Zap,
  Shield,
  BarChart3,
  PlayCircle,
  ArrowRight,
  CheckCircle,
  Star
} from 'lucide-react';

// Floating particles component
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className={`absolute w-2 h-2 bg-gradient-to-r from-[#1E88E5]/20 to-[#6E3CBC]/20 rounded-full animate-pulse`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}
        />
      ))}
    </div>
  );
};

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50/30 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_agents-connect/artifacts/f4wofrcv_AGENT_ADVANCER_senza_sfondo_-removebg-preview.png"
              alt="AgentsPlus Logo"
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold text-gray-900">AgentsPlus</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('platform')} 
              className="text-gray-700 hover:text-[#1E88E5] transition-colors duration-200"
            >
              Scopri la piattaforma
            </button>
            <button className="text-gray-700 hover:text-[#1E88E5] transition-colors duration-200">
              Accedi
            </button>
            <button className="text-gray-700 hover:text-[#1E88E5] transition-colors duration-200">
              Registrati
            </button>
            <Button className="bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-200">
              Entra Gratis
            </Button>
          </nav>

          {/* Mobile menu button */}
          <Button variant="ghost" className="md:hidden">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="hero" 
        className={`relative pt-32 pb-20 px-6 transition-all duration-1000 ${isVisible.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} overflow-hidden`}
      >
        <FloatingParticles />
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_agents-connect/artifacts/f4wofrcv_AGENT_ADVANCER_senza_sfondo_-removebg-preview.png"
                alt="AgentsPlus Logo"
                className="h-16 w-auto opacity-90"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              La nuova era del{' '}
              <span className="bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] bg-clip-text text-transparent">
                recruiting immobiliare
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed font-medium">
              AgentsPlus collega agenzie e candidati in modo intelligente, veloce e trasparente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={() => scrollToSection('come-funziona')}
                size="lg" 
                className="bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] text-white px-10 py-5 text-xl rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 font-semibold group"
              >
                Scopri la piattaforma
                <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
              <Button 
                onClick={() => scrollToSection('cta')}
                size="lg" 
                variant="outline"
                className="border-2 border-[#1E88E5] text-[#1E88E5] px-10 py-5 text-xl rounded-xl hover:bg-[#1E88E5] hover:text-white transition-all duration-300 transform hover:-translate-y-2 font-semibold group"
              >
                Inizia ora gratis
                <Zap className="ml-3 h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
              </Button>
            </div>
          </div>
          
          {/* Hero Mockup */}
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-8 shadow-2xl">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="h-4 bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] rounded-full mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1E88E5] to-[#6E3CBC] rounded-full"></div>
                    <div className="space-y-2 flex-1">
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-2 bg-gray-200 rounded"></div>
                    <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Come Funziona Section */}
      <section 
        id="come-funziona" 
        className={`py-20 px-6 bg-gray-50 transition-all duration-1000 delay-200 ${isVisible['come-funziona'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1E88E5]/10 text-[#1E88E5] border-[#1E88E5]/20">
              Come funziona
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Semplice, intelligente, efficace
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tre passaggi per rivoluzionare il tuo approccio al recruiting immobiliare
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="group p-8 text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-0 bg-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="w-16 h-16 bg-gradient-to-br from-[#1E88E5] to-[#6E3CBC] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <Search className="h-8 w-8 text-white group-hover:rotate-12 transition-transform duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1E88E5] transition-colors duration-300">Trova la tua agenzia ideale</h3>
              <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                Algoritmi intelligenti che matchano le tue competenze con le opportunità perfette per te
              </p>
            </Card>

            <Card className="group p-8 text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-0 bg-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="w-16 h-16 bg-gradient-to-br from-[#1E88E5] to-[#6E3CBC] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <Briefcase className="h-8 w-8 text-white group-hover:rotate-12 transition-transform duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1E88E5] transition-colors duration-300">Gestisci colloqui e match in un click</h3>
              <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                Dashboard intuitiva per organizzare appuntamenti, valutare candidati e chiudere assunzioni
              </p>
            </Card>

            <Card className="group p-8 text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-0 bg-white relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="w-16 h-16 bg-gradient-to-br from-[#1E88E5] to-[#6E3CBC] rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <TrendingUp className="h-8 w-8 text-white group-hover:rotate-12 transition-transform duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1E3CBC] transition-colors duration-300">Monitora la tua crescita professionale</h3>
              <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                Analytics dettagliate per tracciare progressi, performance e costruire la tua reputazione
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Target Section */}
      <section 
        id="target" 
        className={`py-20 px-6 transition-all duration-1000 delay-300 ${isVisible.target ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#6E3CBC]/10 text-[#6E3CBC] border-[#6E3CBC]/20">
              Per chi è pensato
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              La tua carriera, il nostro obiettivo
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group">
              <Card className="p-8 h-full hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-blue-50 to-white">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center mb-6">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Per chi vuole iniziare</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Formazione gratuita, mentoring personalizzato e supporto per i primi passi nel settore immobiliare. Inizia con sicurezza.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    Corsi di formazione gratuiti
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    Mentoring da esperti
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    Match con agenzie formative
                  </li>
                </ul>
              </Card>
            </div>

            <div className="group">
              <Card className="p-8 h-full hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-blue-50 to-white">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1E88E5] to-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Per agenti esperti</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Espandi il tuo network, trova nuove opportunità di crescita e costruisci il team dei tuoi sogni.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    Network professionale
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    Opportunità di team building
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                    Analytics avanzate
                  </li>
                </ul>
              </Card>
            </div>

            <div className="group">
              <Card className="p-8 h-full hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-purple-50 to-white">
                <div className="w-12 h-12 bg-gradient-to-br from-[#6E3CBC] to-purple-600 rounded-xl flex items-center justify-center mb-6">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Per titolari e recruiter</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Trova i migliori talenti, ottimizza il processo di selezione e costruisci team vincenti con AI intelligente.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-purple-500 mr-2 flex-shrink-0" />
                    Matching AI-powered
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-purple-500 mr-2 flex-shrink-0" />
                    Dashboard di gestione
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-purple-500 mr-2 flex-shrink-0" />
                    Report dettagliati
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Vantaggi Section */}
      <section 
        id="vantaggi" 
        className={`py-20 px-6 bg-gray-50 transition-all duration-1000 delay-400 ${isVisible.vantaggi ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1E88E5]/10 text-[#1E88E5] border-[#1E88E5]/20">
              Vantaggi Principali
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Perché scegliere AgentsPlus
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#1E88E5] to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Connessione immediata</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Match istantaneo tra agenzie e candidati
              </p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-[#6E3CBC] to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Matching intelligente</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                AI che comprende competenze e necessità
              </p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Supporto gratuito</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Community formativa sempre disponibile
              </p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Crescita misurabile</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Dashboard per monitorare i progressi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Testimonianze Section */}
      <section 
        id="community" 
        className={`py-20 px-6 transition-all duration-1000 delay-500 ${isVisible.community ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-500/10 text-green-600 border-green-500/20">
              Community & Testimonianze
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Unisciti a chi ha già trasformato la propria carriera
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Migliaia di professionisti hanno già scelto AgentsPlus per crescere nel settore immobiliare
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-blue-50/50">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face&auto=format" 
                     alt="Marco R." className="w-12 h-12 rounded-full object-cover" />
              </div>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">
                "AgentsPlus mi ha aiutato a trovare l'agenzia perfetta in soli 2 settimane. Il processo è stato trasparente e professionale."
              </p>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">Marco Rossi</p>
                <p className="text-gray-500">Agente Immobiliare - Milano</p>
              </div>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-purple-50/50">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                <img src="https://images.unsplash.com/photo-1494790108755-2616b612b002?w=100&h=100&fit=crop&crop=face&auto=format" 
                     alt="Laura M." className="w-12 h-12 rounded-full object-cover" />
              </div>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">
                "Grazie alla formazione gratuita di AgentsPlus ho iniziato la mia carriera con sicurezza. Ora guido un team di 5 agenti!"
              </p>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">Laura Marchetti</p>
                <p className="text-gray-500">Team Leader - Roma</p>
              </div>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-green-50/50">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face&auto=format" 
                     alt="Alessandro P." className="w-12 h-12 rounded-full object-cover" />
              </div>
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">
                "Come titolare di agenzia, AgentsPlus mi ha fatto risparmiare 70% del tempo nel recruiting. Candidati di qualità garantita."
              </p>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">Alessandro Pozzi</p>
                <p className="text-gray-500">CEO Immobiliare Pozzi - Torino</p>
              </div>
            </Card>
          </div>

          {/* Stats Section */}
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="group">
              <div className="text-4xl font-bold text-[#1E88E5] mb-2 group-hover:scale-110 transition-transform duration-300">2.500+</div>
              <p className="text-gray-600">Professionisti attivi</p>
            </div>
            <div className="group">
              <div className="text-4xl font-bold text-[#6E3CBC] mb-2 group-hover:scale-110 transition-transform duration-300">150+</div>
              <p className="text-gray-600">Agenzie partner</p>
            </div>
            <div className="group">
              <div className="text-4xl font-bold text-green-500 mb-2 group-hover:scale-110 transition-transform duration-300">95%</div>
              <p className="text-gray-600">Tasso di soddisfazione</p>
            </div>
            <div className="group">
              <div className="text-4xl font-bold text-orange-500 mb-2 group-hover:scale-110 transition-transform duration-300">72h</div>
              <p className="text-gray-600">Tempo medio di match</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section 
        id="demo" 
        className={`py-20 px-6 transition-all duration-1000 delay-500 ${isVisible.demo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <Badge className="mb-4 bg-[#6E3CBC]/10 text-[#6E3CBC] border-[#6E3CBC]/20">
            Guarda come funziona
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Trasforma il recruiting immobiliare
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Guarda come AgentsPlus trasforma il modo di fare recruiting nel real estate.
          </p>
          
          <div className="relative">
            <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center group cursor-pointer hover:shadow-2xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1E88E5] to-[#6E3CBC] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <PlayCircle className="h-10 w-10 text-white" />
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#1E88E5]/10 to-[#6E3CBC]/10 rounded-3xl pointer-events-none"></div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        id="cta" 
        className={`py-20 px-6 bg-gradient-to-br from-[#1E88E5] to-[#6E3CBC] text-white transition-all duration-1000 delay-600 ${isVisible.cta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Inizia oggi a costruire la tua carriera nel real estate
          </h2>
          <p className="text-xl mb-12 text-blue-100">
            Unisciti a migliaia di professionisti che hanno già scelto AgentsPlus
          </p>
          
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-[#1E88E5] px-8 py-4 text-lg rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
            >
              Sono un candidato
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white px-8 py-4 text-lg rounded-xl hover:bg-white hover:text-[#1E88E5] transition-all duration-300 transform hover:-translate-y-1"
            >
              Sono un'agenzia immobiliare
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Logo & Mission */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_agents-connect/artifacts/f4wofrcv_AGENT_ADVANCER_senza_sfondo_-removebg-preview.png"
                  alt="AgentsPlus Logo"
                  className="h-10 w-auto brightness-0 invert"
                />
                <span className="text-2xl font-bold">AgentsPlus</span>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
                La prima piattaforma intelligente che rivoluziona il recruiting nel settore immobiliare. 
                Connessioni autentiche, crescita garantita.
              </p>
              <div className="flex space-x-4">
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white p-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </Button>
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white p-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </Button>
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white p-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.719-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.731.099.120.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.159-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.357-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001.012.001z"/>
                  </svg>
                </Button>
              </div>
            </div>

            {/* Links Utili */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Piattaforma</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Come funziona</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Prezzi</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Demo gratuita</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Success stories</a></li>
              </ul>
            </div>

            {/* Supporto */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Supporto</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Centro assistenza</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Contatti</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors hover:translate-x-1 transform duration-200 inline-block">Termini di servizio</a></li>
              </ul>
            </div>
          </div>
          
          {/* Call to Action Footer */}
          <div className="bg-gradient-to-r from-[#1E88E5]/10 to-[#6E3CBC]/10 rounded-2xl p-8 mb-8 text-center">
            <h3 className="text-2xl font-bold mb-2">Pronto a rivoluzionare la tua carriera?</h3>
            <p className="text-gray-300 mb-6">Unisciti a migliaia di professionisti che hanno già scelto AgentsPlus</p>
            <Button className="bg-gradient-to-r from-[#1E88E5] to-[#6E3CBC] text-white px-8 py-3 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              Accedi alla piattaforma
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between text-sm text-gray-400">
            <p>© 2024 AgentsPlus. Tutti i diritti riservati.</p>
            <p className="mt-4 md:mt-0">Made with ❤️ by Antonio Ceruso International SRL</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;